import {
    NetworkError,
    generateRouteHandler,
    makeCanonicalUrl,
    permuteUrlRules,
} from '@jet-app/music-classical/utilities';
import {
    LIBRARY_LIST_PAGE_TYPE_ALLOWLIST,
    LibraryListPageType,
    makeLibraryListPageIntent,
    type LibraryListPageIntent,
} from './LibraryListPageIntent';
import type { IntentController } from '@jet/environment/dispatching';
import type { RouteProvider } from '@jet/environment/routing';
import type { ObjectGraph } from '@jet/environment/dependencies';
import { IntentKind } from '@jet-app/music-classical/intents';
import {
    requestViewPage,
    requestViewPageWithQuery,
} from '@jet-app/music-classical/api/request-view-page';
import type { LibraryListPage } from '@jet-app/music-classical/models/pages/library/list';
import { renderLibraryListPage } from './renderLibraryListPage';
import {
    isEmptyPage,
    renderEmptyPage,
} from '@jet-app/music-classical/utilities/transforms/empty-view';
import { pageSortByChangeEventTrigger } from '../../common/page-invalidation-rules';
import { requiresSignIn } from '../utils/requires-sign-in';

const canonicalUrlRule = {
    path: '/library/{pageType}',
    query: [],
};

export const LibraryListPageIntentController: IntentController<LibraryListPageIntent> &
    RouteProvider = requiresSignIn({
    $intentKind: IntentKind.LIBRARY_LIST_PAGE,

    routes: function (objectGraph: ObjectGraph) {
        return [
            {
                rules: permuteUrlRules(canonicalUrlRule),
                handler: (url, handlerParams) => {
                    const pageType =
                        handlerParams.pageType as LibraryListPageType;
                    if (LIBRARY_LIST_PAGE_TYPE_ALLOWLIST.includes(pageType)) {
                        return generateRouteHandler(
                            makeLibraryListPageIntent,
                            objectGraph,
                        ).apply(this, [
                            url,
                            handlerParams,
                            { isLibrary: true },
                        ]);
                    }
                },
            },
        ];
    },

    async perform(
        intent: LibraryListPageIntent,
        objectGraph: ObjectGraph,
    ): Promise<LibraryListPage> {
        const canonicalURL = makeCanonicalUrl(
            canonicalUrlRule,
            intent,
            objectGraph,
        );

        let apiResponse;

        if (intent.metadata?.url) {
            apiResponse = await requestViewPageWithQuery(
                intent.metadata.url,
                objectGraph,
            );
        } else {
            apiResponse = await requestViewPage(
                `favorites/${intent.pageType}`,
                objectGraph,
            );
        }

        if (!apiResponse?.body) {
            throw NetworkError.noContent();
        }

        if (isEmptyPage(apiResponse)) {
            return renderEmptyPage<LibraryListPage>(
                canonicalURL,
                apiResponse,
                objectGraph,
            );
        }

        const page = renderLibraryListPage(
            canonicalURL,
            apiResponse,
            objectGraph,
        );

        return {
            ...page,
            invalidationRules: {
                eventTriggers: [pageSortByChangeEventTrigger(intent)],
            },
        };
    },
});
